import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import McpProviderInstructions, {
  InstructionSteps,
  Subtitle,
  TextBlock,
} from "components/integrations/McpProviderInstructions";
import {
  MCP_PROVIDERS,
  type McpProvider,
  type McpProviderId,
} from "components/integrations/mcpProviders";
import Alert from "components/ui/alert/Alert";
import Button from "components/ui/button/Button";
import Collapsible from "components/ui/collapsible/Collapsible";
import EmptyState from "components/ui/empty_state/EmptyState";
import CentredSpinner from "lib/core_components/CentredSpinner";
import type { McpClientWithSecret } from "lib/hooks/api/auth/mcp/schemas";
import { useMcpClients } from "lib/hooks/api/auth/mcp/useMcpClients";
import { useCompanyAuthConfig } from "lib/hooks/api/company/useCompanyAuthConfig";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { McpConnectionDetails } from "./ConnectionFields";
import { CredentialSecretModal } from "./CredentialSecretModal";
import { GenerateCredentialModal } from "./GenerateCredentialModal";
import { InviteItAdminModal } from "./InviteItAdminModal";
import { McpCredentialCard } from "./McpCredentialCard";

// The generic tile is the fallback: it prompts for a tool name, which suits anything not listed.
const DEFAULT_PROVIDER_ID: McpProviderId = "other";

// Names the guidance's connection details group after its heading.
const CONNECTION_DETAILS_TITLE_ID = "mcp-connection-details-title";

/**
 * The provider the generate flow should register against. Every candidate must carry `selfServe`,
 * including the fallback — a provider without it would render a Generate button that does nothing.
 */
function selfServeProvider(id: McpProviderId | undefined): McpProvider | undefined {
  const selfServeProviders = MCP_PROVIDERS.filter((provider) => provider.selfServe);
  return (
    selfServeProviders.find((provider) => provider.id === id) ??
    selfServeProviders.find((provider) => provider.id === DEFAULT_PROVIDER_ID) ??
    selfServeProviders[0]
  );
}

/** A newly issued secret, and why it was issued — the modal words itself differently for a rotation. */
type IssuedCredential = {
  credential: McpClientWithSecret;
  isRotation: boolean;
};

type Props = {
  /** Which tile the user arrived from, so the generate flow knows what it's connecting. */
  toolId?: McpProviderId;
};

export function McpCredentialsPage({ toolId }: Props): JSX.Element {
  const provider = selfServeProvider(toolId);

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  // One piece of state: the secret is only ever shown with the reason it was issued.
  const [issued, setIssued] = useState<IssuedCredential | null>(null);

  const { data: credentials, isLoading, isFetching, isError, isSuccess } = useMcpClients();
  const { data: currentUser } = useCurrentUser();
  const { data: authConfig, isLoading: isLoadingAuthConfig } = useCompanyAuthConfig();

  const hasCredentials = (credentials?.length ?? 0) > 0;

  // auth-service enforces the company's MCP restriction. A member's actions wait for the setting to
  // load, then follow the last value read; if it never loads, the server explains.
  const isCompanyAdmin = Boolean(currentUser?.isCompanyAdmin);
  const adminOnly = authConfig?.restrictMcpCredentialsToAdmins === true;
  const canManage = isCompanyAdmin || (!isLoadingAuthConfig && !adminOnly);
  // With the restriction on, a member's invitee couldn't do the setup, so only an admin can invite,
  // as an admin. Waits for the setting so an invite never goes out with the wrong role.
  const canInviteItAdmin = !isLoadingAuthConfig && (!adminOnly || isCompanyAdmin);
  const restrictedForViewer = adminOnly && !isCompanyAdmin;

  function showIssuedCredential(credential: McpClientWithSecret, isRotation: boolean) {
    setIssued({ credential, isRotation });
    setShowGenerateModal(false);
  }

  function renderCredentials() {
    // Spin while refetching an empty cached list so an in-flight refresh never reads as "none".
    if (isLoading || isLoadingAuthConfig || (isFetching && !hasCredentials)) {
      return <CentredSpinner />;
    }

    if (isError) {
      return (
        <CentredState>
          <EmptyState
            size="large"
            type="loadError"
            heading="Could not load your credentials"
            description="There was an error loading your MCP credentials. Please try again later."
          />
        </CentredState>
      );
    }

    if (!credentials?.length) {
      return (
        <CentredState>
          <EmptyState
            size="large"
            type="noResults"
            heading="No credentials yet"
            description={
              restrictedForViewer
                ? "A company admin can generate a client ID and secret to connect an AI tool that can't sign itself up."
                : "Generate a client ID and secret to connect an AI tool that can't sign itself up."
            }
          />
        </CentredState>
      );
    }

    return (
      <Flex column gap={16}>
        {credentials.map((credential) => (
          <McpCredentialCard
            key={credential.client_id}
            credential={credential}
            canManage={canManage}
            onSecretRotated={(rotated) => showIssuedCredential(rotated, true)}
          />
        ))}
      </Flex>
    );
  }

  return (
    <PageContainer column gap={24}>
      <Flex justifyContent="space-between" alignItems="center" gap={16}>
        <Flex column gap={8}>
          <Text h2>MCP credentials</Text>
          <Text description>
            Some AI tools can connect with just the server URL — they register themselves
            automatically. Tools that can&apos;t need a client ID and secret, which{" "}
            {restrictedForViewer ? "a company admin" : "you"} can generate here.
          </Text>
        </Flex>
        {provider && (
          <Flex gap={8} flexShrink={0}>
            {/* Secondary beside the page's primary action, as Team management's Invite user is. */}
            <Button
              buttonType="secondary"
              icon="plus"
              onClick={() => setShowInviteModal(true)}
              disabled={!canInviteItAdmin}
            >
              Invite IT admin
            </Button>
            <Button onClick={() => setShowGenerateModal(true)} disabled={!canManage}>
              Generate credentials
            </Button>
          </Flex>
        )}
      </Flex>

      {restrictedForViewer && (
        <Alert type="info">
          Only company admins can manage MCP credentials at your company, including their callback
          URLs. Ask a company admin if you need one.
        </Alert>
      )}

      {/* Waits for a successful load rather than merely a finished one. `defaultOpen` is
          uncontrolled, so whatever it reads on mount sticks: a failed first fetch looks like "no
          credentials", and React Query's retry then fills the list in underneath an expanded
          guide. */}
      {isSuccess && (
        <Collapsible title="How to set this up" defaultOpen={!hasCredentials} isBordered>
          <>
            <InstructionSteps>
              <li>
                {restrictedForViewer ? (
                  "Ask a company admin to generate credentials and send you the client ID and secret."
                ) : (
                  <>
                    Generate credentials. You&apos;ll get a client ID and secret, along with
                    everything else your AI tool needs to connect. The secret is shown once, so copy
                    it before you close the dialog.
                  </>
                )}
              </li>
              <li>
                In your AI tool, add Stotles as a custom MCP server using the server URL from the
                connection details below, then paste in the client ID, secret and whatever else it
                asks for.
              </li>
              <li>
                Some tools only give you a callback URL once the connection exists. If yours does,{" "}
                {restrictedForViewer ? "ask a company admin to add it" : "come back and add it"} to
                the credential here.
              </li>
              <li>Sign in to Stotles when your tool prompts you, to approve access.</li>
            </InstructionSteps>

            {/* Ahead of a tool's own steps, which point at these as "your connection details". */}
            <ConnectionDetails role="group" aria-labelledby={CONNECTION_DETAILS_TITLE_ID}>
              <Subtitle id={CONNECTION_DETAILS_TITLE_ID}>Connection details</Subtitle>
              <TextBlock tight>
                Authentication: OAuth 2.0, configured manually. Each user still signs in to their
                own Stotles account, so access follows their existing Stotles permissions.
              </TextBlock>
              <McpConnectionDetails />
            </ConnectionDetails>

            {/* Only for a named platform. The generic tile's guidance covers connecting with the
                server URL alone, which is the path that needs no credential at all. */}
            {provider?.selfServe?.toolName && <McpProviderInstructions provider={provider} />}
          </>
        </Collapsible>
      )}

      {renderCredentials()}

      {showGenerateModal && provider && (
        <GenerateCredentialModal
          provider={provider}
          onClose={() => setShowGenerateModal(false)}
          onCreated={(credential) => showIssuedCredential(credential, false)}
        />
      )}

      {showInviteModal && provider && (
        <InviteItAdminModal
          provider={provider}
          asAdmin={adminOnly}
          onClose={() => setShowInviteModal(false)}
        />
      )}

      {issued && (
        <CredentialSecretModal
          credential={issued.credential}
          isRotation={issued.isRotation}
          onClose={() => setIssued(null)}
        />
      )}
    </PageContainer>
  );
}

// Spaced like the steps above it, so a tool's own guidance sits a full gap below the fields.
const ConnectionDetails = styled.div({
  "&:not(:last-child)": { marginBottom: 24 },
});

// Settings pages carry their own page padding — there is no shared layout that applies it. 16px
// matches the integrations index this page is reached from, so the heading doesn't shift sideways
// when you navigate between the two.
const PageContainer = styled(Flex)({
  margin: 16,
});

// `EmptyState` is a fixed-width block that relies on its parent to place it, so a page-level one
// needs centring or it strands itself against the left edge.
const CentredState = styled(Flex)({
  justifyContent: "center",
});
